import React, { forwardRef } from "react";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { Button, Card, Divider, Skeleton, notification } from "antd";
import { create, get } from "lodash";
import styled from "styled-components";

import { GET_FULFILLMENT_PRODUCT_MOCKUP_BY_PRODUCT } from "../../graphql/queries";
import { arrInvalid, handleError, reducerFn } from "../../helper";
import MediaSelectorButton from "../supplier/MediaSelectorButton";
import {
  CREATE_FULFILLMENT_PRODUCT_MOCKUPS,
  UPDATE_FULFILLMENT_PRODUCT_MOCKUPS,
} from "../../graphql/mutations";

// printer: FASTUS, ff: Mango
export const PINTERS_NEED_MOCKUP = ["FASTUS"];
function FulfillmentProductMockup({ productBase, product, folder }, ref) {
  const printer = get(productBase, "fulfillment.printer");
  const productId = get(product, "id");

  const [state, setState] = React.useReducer(reducerFn, {
    values: [
      { id: "", type: "Front", file: [] },
      { id: "", type: "Back", file: [] },
    ],
  });

  const needHide =
    !printer ||
    typeof printer !== "string" ||
    !PINTERS_NEED_MOCKUP.includes(printer.toUpperCase());

  const { data, loading } = useQuery(
    GET_FULFILLMENT_PRODUCT_MOCKUP_BY_PRODUCT,
    {
      variables: {
        productId,
      },
      skip: !productId || needHide,
    },
  );

  const isCreated =
    (get(data, "getFulfillmentProductMockupByProduct") || []).length > 0;

  const [createFOM, { loading: l2 }] = useMutation(
    CREATE_FULFILLMENT_PRODUCT_MOCKUPS,
    {
      onCompleted: () => {
        notification.success({
          message: "Create mockup for fulfillment success",
        });
      },
      onError: (error) => {
        notification.error({ message: handleError(error?.toString()) });
      },
    },
  );
  const [updateFOM, { loading: l3 }] = useMutation(
    UPDATE_FULFILLMENT_PRODUCT_MOCKUPS,
    {
      onCompleted: () => {
        notification.success({
          message: "Update mockup for fulfillment success",
        });
      },
      onError: (error) => {
        notification.error({ message: handleError(error?.toString()) });
      },
    },
  );

  React.useEffect(() => {
    const nodes = get(data, "getFulfillmentProductMockupByProduct") || [];
    if (arrInvalid(nodes)) {
      return;
    }

    const values = [];
    for (const item of nodes) {
      if (!item) continue;

      const { file, ...rest } = item;

      values.push({ ...rest, file: file ? [file] : [] });
    }

    setState({ values });
  }, [data]);

  React.useImperativeHandle(ref, () => ({
    getMockups: () => preparePayload(state.values, isCreated, productId),
  }));

  const changedRef = React.useRef(false);
  const handleFileChange = React.useCallback(
    (index) => (value) => {
      changedRef.current = true;
      setState((p) => {
        p.values[index] = { ...p.values[index], file: value };
        return { ...p };
      });
    },
    [],
  );

  const handleUpdate = React.useCallback(() => {
    const payload = preparePayload(state.values, isCreated, productId);

    if (payload.length > 0) {
      if (isCreated) {
        updateFOM({
          variables: {
            input: payload,
          },
        });
      } else {
        createFOM({
          variables: {
            input: payload,
          },
        });
      }
    }

  }, [state.values, productId, isCreated, createFOM, updateFOM]);
  if (needHide) {
    return null;
  }

  return (
    <Wrapper>
      <Card title="Mockup for Fulfillment">
        {loading ? (
          <Skeleton />
        ) : (
          state.values.map((value, index) => {
            if (!value) return null;
            return (
              <div className="mockup-item">
                <h3>{value?.type || ""}</h3>
                <MediaSelectorButton
                  accept={"image/*"}
                  buttonType="primary"
                  folder={folder}
                  listType={"picture-card"}
                  prefix="mockups/"
                  multiple={false}
                  value={value?.file || []}
                  isUpdateData
                  onChange={handleFileChange(index)}
                />
              </div>
            );
          })
        )}

        <Divider />

        <Button
          children={`${isCreated ? "Update" : "Create"} Mockup`}
          type="primary"
          onClick={handleUpdate}
          loading={l2 || l3}
          disabled={!changedRef.current}
        />
      </Card>
    </Wrapper>
  );
}

const Wrapper = styled.div``;

function preparePayload(values, isCreated, productId) {
  const payload = [];
  for (const val of values) {
    if (!val) continue;

    const { type, file } = val;
    let fileId = "";
    if (!arrInvalid(file)) {
      let newFile = file.filter(Boolean)[0];
      if (newFile) {
        fileId = newFile.id;
      }
    }

    payload.push({
      productId,
      fileId,
      type,
      id: isCreated ? val.id : undefined,
    });
  }

  return payload;
}

export default forwardRef(FulfillmentProductMockup);
